import React, { createContext, useContext, ReactNode, Dispatch, SetStateAction, useState, useMemo } from 'react';

type FinancialYear = {
  year: string;
  value: number;
};

type FinancialYearContext = {
  financialYearList: FinancialYear;
  setFinancialYear: Dispatch<SetStateAction<FinancialYear>>;
  notMatchingFinancialYear: boolean;
  SetNotMatchingFinancialYear: Dispatch<SetStateAction<boolean>>;
};

const YearContext = createContext<FinancialYearContext | undefined>(undefined);

type FinancialYearProviderProps = {
  children: ReactNode;
};

const getCurrentFinancialYear = (): FinancialYear => {
  try {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const startYear = currentMonth >= 3 ? currentYear : currentYear - 1;
    const endYear = startYear + 1;
    return {
      year: `${startYear}-${endYear}`,
      value: startYear,
    };
  } catch (error) {
    console.error('Error calculating financial year:', error);
    return { year: 'Unknown', value: 0 };
  }
};

const FinancialYearProvider: React.FC<FinancialYearProviderProps> = ({ children }) => {
  const [financialYear, setFinancialYear] = useState<FinancialYear>(getCurrentFinancialYear());
  const [notMatchingFinancialYear, SetNotMatchingFinancialYear] = useState<boolean>(false);

  const value: FinancialYearContext = useMemo(
    () => ({
      financialYearList: financialYear,
      setFinancialYear,
      notMatchingFinancialYear,
      SetNotMatchingFinancialYear,
    }),
    [financialYear, notMatchingFinancialYear],
  );

  return <YearContext.Provider value={value}>{children}</YearContext.Provider>;
};

const useFinancialYearContext = () => {
  const context = useContext(YearContext);
  if (!context) {
    throw new Error('useFinancialYearContext must be used within a FinancialYearProvider');
  }
  return context;
};

export { FinancialYearProvider, useFinancialYearContext };
